import * as React from 'react';

import { FieldValues, Path, PathValue, useController, useFormContext } from 'react-hook-form';
import { Checkbox as ChakraCheckbox } from '@chakra-ui/react';
import { Field, CheckboxControl, CheckboxHiddenInput, CheckboxIndicator, CheckboxLabel, CheckboxRoot, FieldProps } from '../chakra-components';
import { useWatchCallback } from './useWatchCallback';


export interface CheckBoxProps<T extends FieldValues, TPath extends Path<T>> extends Omit<ChakraCheckbox.RootProps, 'value' | 'onChange' | 'checked' | 'defaultChecked' | 'onCheckedChange'> {
	ref?: React.Ref<HTMLLabelElement>;
	linkTo: TPath;
	disabled?: boolean;
	label?: React.ReactNode;
	value?: boolean;
	fieldProps?: FieldProps;
	colorPalette?: 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'danger' | 'blue';
	onWatch?: (value: PathValue<T, TPath>) => void;
}

export type CheckBoxRef = HTMLLabelElement;

export const CheckBox = <T extends FieldValues, TPath extends Path<T>>(props: CheckBoxProps<T, TPath> & { ref?: React.Ref<CheckBoxRef> }) => {
	const { ref, fieldProps, disabled, label, linkTo, value, colorPalette = 'blue', onWatch, ...rootProps } = props;

	const { control } = useFormContext<T>();
	const { fieldState, field } = useController<T>({ name: linkTo, control, disabled });

	useWatchCallback(control, linkTo, onWatch);

	const invalid = fieldState.error?.message !== undefined;
	const checked = value ?? field.value ?? false;

	return (
		<Field errorText={fieldState.error?.message} invalid={invalid} {...fieldProps}>
			<CheckboxRoot ref={ref} checked={checked} disabled={disabled} colorPalette={colorPalette} onCheckedChange={(details) => field.onChange(details.checked === true)} {...rootProps}>
				<CheckboxHiddenInput ref={field.ref} name={field.name} onBlur={field.onBlur} disabled={disabled} />
				<CheckboxControl>
					<CheckboxIndicator />
				</CheckboxControl>
				{label && <CheckboxLabel>{label}</CheckboxLabel>}
			</CheckboxRoot>
		</Field >
	);
};